import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/catalyst/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateProviderDirectoryOrganizationMutation } from '@/services/api/providerDirectoryV2Service';

const ORG_TYPES = [
  'HOSPITAL_NETWORK',
  'CLINIC_GROUP',
  'SOLO_PRACTICE',
  'BILLING_COMPANY',
  'RECORDS_COMPANY',
  'PHARMACY_CHAIN',
  'OTHER',
] as const;

export const CreateOrganizationModal = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (orgId: string) => void;
}) => {
  const [createOrg, { isLoading: isSaving }] =
    useCreateProviderDirectoryOrganizationMutation();

  const [name, setName] = useState('');
  const [organizationType, setOrganizationType] = useState<string>('OTHER');
  const [aliasesText, setAliasesText] = useState('');
  const [website, setWebsite] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setOrganizationType('OTHER');
    setAliasesText('');
    setWebsite('');
  }, [open]);

  const aliases = useMemo(() => {
    const raw = aliasesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return raw.length ? raw : [];
  }, [aliasesText]);

  const canSave = Boolean(name.trim());

  const handleSave = async () => {
    if (!canSave) return;
    try {
      const org = await createOrg({
        name: name.trim(),
        organization_type: organizationType,
        aliases,
        website: website.trim() || null,
      }).unwrap();
      toast.success('Organization created');
      onCreated(org.id);
      onClose();
    } catch (err) {
      console.error('Failed to create org', err);
      toast.error('Failed to create organization');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-2xl max-h-[calc(100vh-24px)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>
            Organizations are user-managed. Create one when it helps group
            locations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-xs font-medium text-zinc-700">Name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-700">Type</div>
              <select
                className="h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm w-full"
                value={organizationType}
                onChange={(e) => setOrganizationType(e.target.value)}
              >
                {ORG_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-zinc-700">Website</div>
              <Input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="Optional…"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium text-zinc-700">Aliases</div>
            <Input
              value={aliasesText}
              onChange={(e) => setAliasesText(e.target.value)}
              placeholder="Comma-separated…"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || isSaving}>
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
